import type { ReactNode } from "react";
import { Link, useLocation } from "react-router";
import { Images, Zap } from "lucide-react";
import { useIntl } from "react-intl";

import { buildBrandPath } from "@/hooks/use-route-brand";
import { cn } from "@/lib/utils";
import { Card, CardContent } from "@runcheetah/ui";

type BrandSettingsShellProps = {
  brand: {
    id: string;
  };
  activeSection: "autopilot" | "asset-creation" | "billing";
  billingEnabled: boolean;
  children: ReactNode;
};

export function BrandSettingsShell({
  brand,
  activeSection,
  billingEnabled,
  children,
}: BrandSettingsShellProps) {
  const intl = useIntl();
  const location = useLocation();
  const effectiveActiveSection =
    activeSection === "autopilot" && location.hash === "#asset-creation"
      ? "asset-creation"
      : activeSection;

  const autopilotItem = {
    href: buildBrandPath(brand.id, "/settings#autopilot"),
    label: intl.formatMessage({
      id: "brandSettings.nav.autopilot",
      defaultMessage: "Autopilot",
    }),
  };
  const settingsNavItems = [
    {
      id: "asset-creation" as const,
      href: buildBrandPath(brand.id, "/settings#asset-creation"),
      label: intl.formatMessage({
        id: "brandSettings.nav.assetCreation",
        defaultMessage: "Asset creation",
      }),
      icon: Images,
    },
    ...(billingEnabled
      ? [
          {
            id: "billing" as const,
            href: buildBrandPath(brand.id, "/settings/billing"),
            label: intl.formatMessage({
              id: "brandSettings.nav.billing",
              defaultMessage: "Billing",
            }),
            icon: null,
          },
        ]
      : []),
  ];

  return (
    <div className="w-full space-y-6 px-6 py-6">
      <div className="grid gap-6 xl:grid-cols-[220px_minmax(0,1fr)] xl:items-start">
        <Card className="border-0 bg-card/95 shadow-sm lg:sticky lg:top-6">
          <CardContent className="p-3">
            <nav
              className="flex flex-col gap-1"
              aria-label={intl.formatMessage({
                id: "brandSettings.nav.ariaLabel",
                defaultMessage: "Settings sections",
              })}
            >
              <Link
                to={autopilotItem.href}
                aria-current={effectiveActiveSection === "autopilot" ? "page" : undefined}
                className={cn(
                  "flex items-center gap-2 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors",
                  effectiveActiveSection === "autopilot"
                    ? "bg-muted text-foreground"
                    : "text-muted-foreground hover:bg-muted/70 hover:text-foreground",
                )}
              >
                <Zap
                  className={cn(
                    "size-4 shrink-0",
                    effectiveActiveSection === "autopilot"
                      ? "text-foreground"
                      : "text-muted-foreground",
                  )}
                  strokeWidth={effectiveActiveSection === "autopilot" ? 2.2 : 2}
                />
                <span>{autopilotItem.label}</span>
              </Link>

              <div className="px-3 pt-3 text-xs font-semibold uppercase tracking-[0.14em] text-muted-foreground">
                {intl.formatMessage({
                  id: "brandSettings.nav.label",
                  defaultMessage: "Settings",
                })}
              </div>

              {settingsNavItems.map((item) => {
                const isActive = item.id === effectiveActiveSection;
                const Icon = item.icon;

                return (
                  <Link
                    key={item.href}
                    to={item.href}
                    aria-current={isActive ? "page" : undefined}
                    className={cn(
                      "flex items-center justify-between rounded-lg px-3 py-2.5 text-sm font-medium transition-colors",
                      isActive
                        ? "bg-muted text-foreground"
                        : "text-muted-foreground hover:bg-muted/70 hover:text-foreground",
                    )}
                  >
                    <span className="flex items-center gap-2">
                      {Icon ? (
                        <Icon
                          className={cn(
                            "size-4 shrink-0",
                            isActive ? "text-foreground" : "text-muted-foreground",
                          )}
                          strokeWidth={isActive ? 2.2 : 2}
                        />
                      ) : null}
                      <span>{item.label}</span>
                    </span>
                  </Link>
                );
              })}
            </nav>
          </CardContent>
        </Card>

        <div className="min-w-0 w-full xl:px-4">
          <div className="mx-auto w-full max-w-[920px]">{children}</div>
        </div>
      </div>
    </div>
  );
}
